import React, { useEffect, useState } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useLocation } from "react-router-dom";
import Header from "./common/Header";
import Footer from "./common/Footer";
import MainRoutes from "./routes/MainRoutes";
import TripProcessPage from "./components/tripProcess";
import ScrollToTopButton from "./components/ScrollToTopButton";
import './i181n'; // Import i18n configuration

// Sync auth from localStorage to sessionStorage once at bootstrap (before first render).
// Ensures new tabs have sessionStorage populated when protected routes run.
function syncAuthFromLocalStorageBootstrap() {
  if (typeof window === "undefined") return;
  if (sessionStorage.getItem("auth") || !localStorage.getItem("auth")) return;
  const authData = localStorage.getItem("auth");
  const email = localStorage.getItem("email");
  const userid = localStorage.getItem("userid");
  const key = localStorage.getItem("key");
  const name = localStorage.getItem("name");
  const phone = localStorage.getItem("phone");
  const fname = localStorage.getItem("fname");
  const lname = localStorage.getItem("lname");
  const countryid = localStorage.getItem("countryid");
  const code = localStorage.getItem("code");
  const pass = localStorage.getItem("pass");
  const city_id = localStorage.getItem("city_id");
  const city_cur = localStorage.getItem("city_cur");
  const stripe_cust_id = localStorage.getItem("stripe_cust_id");
  const profileImage = localStorage.getItem("profileImage");
  const tabValue = localStorage.getItem("tabValue");
  if (authData) sessionStorage.setItem("auth", authData);
  if (email) sessionStorage.setItem("email", email);
  if (userid) sessionStorage.setItem("userid", userid);
  if (key) sessionStorage.setItem("key", key);
  if (name) sessionStorage.setItem("name", name);
  if (phone) sessionStorage.setItem("phone", phone);
  if (fname) sessionStorage.setItem("fname", fname);
  if (lname) sessionStorage.setItem("lname", lname);
  if (countryid) sessionStorage.setItem("countryid", countryid);
  if (code) sessionStorage.setItem("code", code);
  if (pass) sessionStorage.setItem("pass", pass);
  if (city_id) sessionStorage.setItem("city_id", city_id);
  if (city_cur) sessionStorage.setItem("city_cur", city_cur);
  if (stripe_cust_id) sessionStorage.setItem("stripe_cust_id", stripe_cust_id);
  if (profileImage) sessionStorage.setItem("profileImage", profileImage);
  if (tabValue) sessionStorage.setItem("tabValue", tabValue);
}
syncAuthFromLocalStorageBootstrap();

function App() {
  // Check both sessionStorage and localStorage for auth
  const checkAuth = () => {
    return !!(
      sessionStorage.getItem("auth") || 
      sessionStorage.getItem("email") || 
      sessionStorage.getItem("userid") || 
      sessionStorage.getItem("key") ||
      localStorage.getItem("auth") || 
      localStorage.getItem("email") || 
      localStorage.getItem("userid") || 
      localStorage.getItem("key")
    );
  };

  const [isAuthenticated, setIsAuthenticated] = useState(checkAuth());
  const location = useLocation();
  
  // Check if current route is standalone page (booking, profile, login, or register iframe)
  const isStandalonePage = location.pathname === "/booking-iframe" || 
                           location.pathname === "/profile-iframe" ||
                           location.pathname === "/login-iframe" ||
                           location.pathname === "/register-iframe";

  // Sync auth from localStorage to sessionStorage if needed (e.g. after storage event)
  useEffect(() => {
    syncAuthFromLocalStorageBootstrap();
    setIsAuthenticated(checkAuth());
  }, []);

  // Listen for storage events from other tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      // When localStorage changes in another tab, sync to sessionStorage
      if (e.key === "auth" || e.key === "email" || e.key === "userid" || e.key === "key") {
        if (e.newValue) {
          // Auth data added/updated in another tab
          if (e.key && e.newValue) {
            sessionStorage.setItem(e.key, e.newValue);
          }
          setIsAuthenticated(true);
        } else if (e.oldValue && !e.newValue) {
          // Auth data removed in another tab
          if (e.key) {
            sessionStorage.removeItem(e.key);
          }
          setIsAuthenticated(false);
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);
  
  // Listen for auth status requests from iframe
  useEffect(() => {
    const handleAuthRequest = (event) => {
      console.log('App.js: Received message', event.data);
      
      if (event.data && event.data.type === 'REQUEST_AUTH_STATUS') {
        console.log('App.js: Auth status requested');
        
        // Check both sessionStorage and localStorage
        const auth = sessionStorage.getItem("auth") || localStorage.getItem("auth");
        const email = sessionStorage.getItem("email") || localStorage.getItem("email");
        const userid = sessionStorage.getItem("userid") || localStorage.getItem("userid");
        const apiKey = sessionStorage.getItem("key") || localStorage.getItem("key");
        
        const isLoggedIn = !!(auth || email || userid || apiKey);
        
        console.log('App.js: Sending auth status:', isLoggedIn, { auth, email, userid, apiKey });
        
        // Send response back to iframe
        if (event.source) {
          event.source.postMessage({
            type: 'AUTH_STATUS_RESPONSE',
            isAuthenticated: isLoggedIn
          }, event.origin);
          
          console.log('App.js: Auth status sent to origin:', event.origin);
        }
      }
    };
    
    window.addEventListener('message', handleAuthRequest);
    
    console.log('App.js: Message listener added');
    
    return () => {
      window.removeEventListener('message', handleAuthRequest);
    };
  }, []);
  
  return (
    <>
      {!isStandalonePage && (
        <Header
          isAuthenticated={isAuthenticated}
          setIsAuthenticated={setIsAuthenticated}
        />
      )}
      <div className="App">
        {!isStandalonePage && <TripProcessPage />}
        <MainRoutes setIsAuthenticated={setIsAuthenticated} />
        <ToastContainer />
      </div>
      {!isStandalonePage && <Footer />}
      {!isStandalonePage && <ScrollToTopButton />}
    </>
  );
}

export default App;
